/**
 * Connector Configurations
 * 
 * Data-driven configurations for each supported connector.
 * Add new connectors here to automatically get UI support.
 */

import type { ConnectorConfig, OAuthConnectorConfig } from '@/lib/types/connector'

/**
 * Stripe connector configuration
 */
export const STRIPE_CONNECTOR_CONFIG: Omit<ConnectorConfig, 'status' | 'enabled'> = {
  id: 'stripe',
  name: 'Stripe',
  description: 'Connect Stripe to track payment revenue and attribute it to traffic sources.',
  longDescription: `Stripe is the leading payment processing platform for internet businesses. By connecting your Stripe account to Humblytics, you can automatically track revenue from all your Stripe transactions and attribute it to specific traffic sources, campaigns, and user journeys.

This integration fetches charge data from Stripe's API to match transactions with visitor sessions. No card data or sensitive payment information is ever stored in Humblytics—we only access the transaction amounts and metadata needed for attribution.

With Stripe connected, you'll be able to see which marketing channels, landing pages, and experiments drive the most revenue, helping you optimize your conversion funnel and make data-driven decisions.`,
  brandKey: 'stripe',
  category: 'revenue',
  supportsTest: true,
  fields: [
    {
      id: 'apiKey',
      type: 'password',
      label: 'Stripe API Key',
      placeholder: 'rk_live_... or sk_live_...',
      required: true,
      helpText: 'Recommended: Use a restricted key (rk_) with read-only access to Payment Intents, Invoices, Subscriptions, Customers, Charges, and Account for better security. Full secret keys (sk_) also work.',
      monospace: true,
      externalLink: {
        label: 'Open Stripe Dashboard → API Keys',
        url: 'https://dashboard.stripe.com/apikeys',
      },
      pattern: '^(sk|rk)_(live|test)_[a-zA-Z0-9]+$',
      patternError: 'Please enter a valid Stripe API key (starts with sk_live_, sk_test_, rk_live_, or rk_test_)',
    },
  ],
  helpContent: {
    title: 'About Stripe Integration',
    bullets: [
      'Fetches charges to attribute revenue to traffic sources',
      'No card data is stored in Humblytics',
      'Requires your Stripe secret key for API access',
    ],
    docsUrl: 'https://docs.stripe.com/keys',
    docsLabel: 'Stripe Docs',
  },
}

/**
 * Foxy connector configuration
 */
export const FOXY_CONNECTOR_CONFIG: Omit<ConnectorConfig, 'status' | 'enabled'> = {
  id: 'foxy',
  name: 'Foxy',
  description: 'Connect your Foxy e-commerce store to track transactions via webhooks.',
  longDescription: `Foxy is a powerful e-commerce platform that enables you to add shopping cart functionality to any website. By connecting Foxy to Humblytics, you can track all your e-commerce transactions and attribute revenue to specific traffic sources and campaigns.

Unlike API-based integrations, Foxy uses webhooks to push transaction data to Humblytics in real-time. This means you'll see revenue data as soon as purchases are completed, with no polling delays.

The integration requires setting up a webhook in your Foxy admin panel that sends transaction events to Humblytics. You'll also need to configure an encryption key to ensure secure communication between Foxy and Humblytics.`,
  brandKey: 'foxy',
  category: 'revenue',
  supportsTest: true,
  fields: [
    {
      id: 'storeUrl',
      type: 'url',
      label: 'Foxy Store URL',
      placeholder: 'https://yourstore.foxycart.com',
      required: true,
      helpText: 'Enter your Foxy store URL to enable revenue tracking integration.',
    },
    {
      id: 'encryptionKey',
      type: 'password',
      label: 'Encryption Key',
      placeholder: 'Enter your Foxy webhook encryption key',
      required: true,
      helpText: 'This key is used to validate webhook payloads from Foxy. You\'ll set the same key in your Foxy admin panel.',
    },
    {
      id: 'webhookUrl',
      type: 'readonly',
      label: 'Webhook URL',
      placeholder: 'Loading...',
      required: false,
      helpText: 'Use this URL in your Foxy webhook configuration. Click to copy.',
      monospace: true,
    },
  ],
  helpContent: {
    title: 'About Foxy Integration',
    bullets: [
      'Track transactions from your Foxy store',
      'Revenue data is received via webhooks',
      'Real-time transaction tracking',
    ],
    docsUrl: 'https://www.foxy.io/',
    docsLabel: 'Foxy Website',
    setupSteps: [
      'Log into your Foxy admin panel',
      'Go to Integrations → Webhooks',
      'Enable "JSON Webhook"',
      'Add a new webhook with the URL above',
      'Set the encryption key to the same value you entered above',
      'Select "transaction" events',
      'Include "zoom=items,customer" in the API filter',
      'Save your webhook configuration',
    ],
  },
}

/**
 * Google Analytics 4 connector configuration (OAuth)
 * Not shipping (2026-10-06): kept for the beta-flagged GA4 tab only, never listed.
 */
const GA4_CONNECTOR_CONFIG: Omit<OAuthConnectorConfig, 'status' | 'enabled'> = {
  id: 'ga4',
  name: 'Google Analytics 4',
  description: 'Import your existing GA4 data and pageviews.',
  longDescription: `Google Analytics 4 is Google's latest analytics platform. By connecting your GA4 account to Humblytics, you can import your existing traffic data, pageviews, and events without installing additional tracking scripts.

This integration uses OAuth to securely connect to your Google account and reads data from your GA4 properties via the BigQuery export. All access is read-only and we never access personally identifiable information.

With GA4 connected, you can view your traffic data in Humblytics alongside other data sources like Meta Ads and revenue tracking.`,
  brandKey: 'googleAnalytics',
  category: 'traffic',
  comingSoon: true,
  authType: 'oauth',
  supportsTest: false,
  canReplaceNativeScript: true,
  fields: [],
  helpContent: {
    title: 'About GA4 Integration',
    bullets: [
      'Read-only access to your GA4 properties',
      'Import historical pageview and event data',
      'No additional tracking scripts needed',
    ],
    docsUrl: 'https://support.google.com/analytics/answer/10089681',
    docsLabel: 'GA4 Documentation',
  },
}

/**
 * Google Ads connector configuration (OAuth). Live.
 */
const GOOGLE_ADS_CONNECTOR_CONFIG: Omit<OAuthConnectorConfig, 'status' | 'enabled'> = {
  id: 'google-ads' as any,
  name: 'Google Ads',
  description: 'Track ad spend, impressions, clicks and conversions from Google Search and Display campaigns.',
  brandKey: 'googleAds',
  category: 'advertising',
  comingSoon: false,
  authType: 'oauth',
  supportsTest: false,
  canReplaceNativeScript: false,
  fields: [],
  helpContent: {
    title: 'About Google Ads Integration',
    bullets: [
      'Read-only access to your Google Ads accounts',
      'Track spend, impressions, clicks and conversions',
      'Daily campaign performance breakdown',
    ],
    docsUrl: 'https://ads.google.com',
    docsLabel: 'Google Ads',
  },
}

/**
 * Meta Ads connector configuration (OAuth)
 */
const META_ADS_CONNECTOR_CONFIG: Omit<OAuthConnectorConfig, 'status' | 'enabled'> = {
  id: 'meta',
  name: 'Meta Ads',
  description: 'Track ad spend and campaign performance from Facebook and Instagram.',
  longDescription: `Meta Ads (formerly Facebook Ads) is the advertising platform for Facebook and Instagram. By connecting your Meta Ads account to Humblytics, you can track ad spend, impressions, clicks, and campaign performance alongside your website analytics.

This integration uses OAuth to securely connect to your Meta account with read-only access to your ad accounts. We sync your ad performance data daily so you can attribute revenue and conversions to specific campaigns.

With Meta Ads connected, you'll see a complete picture of your marketing ROI by combining ad spend data with website analytics and revenue tracking.`,
  brandKey: 'meta',
  category: 'advertising',
  authType: 'oauth',
  supportsTest: false,
  canReplaceNativeScript: false,
  fields: [],
  helpContent: {
    title: 'About Meta Ads Integration',
    bullets: [
      'Read-only access to your ad accounts',
      'Track ad spend and performance metrics',
      'Attribute conversions to campaigns',
    ],
    docsUrl: 'https://www.facebook.com/business/help',
    docsLabel: 'Meta Business Help',
  },
}

/**
 * JotForm connector configuration (webhook-based, no credentials needed)
 */
export const JOTFORM_CONNECTOR_CONFIG: Omit<ConnectorConfig, 'status' | 'enabled'> = {
  id: 'jotform',
  name: 'JotForm',
  description: 'Match JotForm submits to sessions Humblytics already tracks.',
  longDescription: `JotForm is a popular online form builder used by millions of businesses to collect leads, applications, and customer data. By connecting JotForm to Humblytics, every form submission is automatically linked to the visitor session that completed it.

This integration works via webhooks. JotForm sends a notification to Humblytics whenever a form is submitted, and Humblytics matches it to the originating session. No form data is stored in Humblytics; only the submission event and its attribution metadata are recorded.

For accurate attribution, add a Hidden Field to your JotForm and set its Unique Name to "hmblViewId". The Humblytics script automatically passes the session ID via URL parameter. No JavaScript required. This creates a direct link between the submission and the visitor's full journey. Traffic source, landing page, and campaign.`,
  brandKey: 'jotform',
  category: 'forms',
  supportsTest: false,
  fields: [
    {
      id: 'webhookUrl',
      type: 'readonly',
      label: 'Webhook URL',
      placeholder: 'Loading...',
      required: false,
      helpText: 'Copy this URL and add it to your JotForm under Settings → Integrations → Webhooks. Click to copy.',
      monospace: true,
    },
  ],
  helpContent: {
    title: 'About JotForm Integration',
    bullets: [
      'Reconcile each submission with the session Humblytics already recorded',
      'Link submissions to the full visitor journey: landing page, campaign and referrer',
      'Add a hidden field to your form for precise session-level attribution',
    ],
    docsUrl: 'https://www.jotform.com/help/268-how-to-use-jotform-webhooks/',
    docsLabel: 'JotForm Webhooks Guide',
    setupSteps: [
      'Copy the Webhook URL above',
      'In JotForm, open your form and go to Settings → Integrations → Webhooks',
      'Click "Add New Webhook", paste the URL, and save',
      'Add a Hidden Field to your form',
      'In Field Details, set the Unique Name to exactly "hmblViewId". Humblytics populates it automatically',
      'Submit a test form entry to verify the integration is working',
    ],
  },
}

/**
 * Typeform connector configuration (API token + auto-registered webhook)
 * Coming soon — implementation complete, awaiting UX refinement.
 */
export const TYPEFORM_CONNECTOR_CONFIG: Omit<ConnectorConfig, 'status' | 'enabled'> = {
  id: 'typeform',
  name: 'Typeform',
  description: 'Match Typeform submits to sessions Humblytics already tracks.',
  comingSoon: true,
  longDescription: `Typeform is a popular form builder known for its conversational interface. By connecting Typeform to Humblytics, every submission is automatically linked to the visitor session that completed the form.

This integration uses a Personal Access Token to auto-register a webhook with Typeform. When someone submits a form, Typeform sends the response to Humblytics and we match it to the visitor's session.

For the most accurate attribution, add a Hidden Field named "humblytics" to your Typeform with the URL parameter ?humblytics=SESSION_ID. This links each submission directly to the visitor's full journey. Traffic source, landing page, and campaign.`,
  brandKey: 'typeform',
  category: 'forms',
  supportsTest: true,
  fields: [
    {
      id: 'apiToken',
      type: 'password',
      label: 'Personal Access Token',
      placeholder: 'tfp_...',
      required: true,
      helpText: 'Generate from your Typeform account → Settings → Personal tokens.',
      externalLink: {
        label: 'Get token',
        url: 'https://admin.typeform.com/account#/section/tokens',
      },
    },
    {
      id: 'webhookUrl',
      type: 'readonly',
      label: 'Webhook URL (auto-registered)',
      placeholder: 'Loading...',
      required: false,
      helpText: 'This URL is automatically registered with Typeform when you save your token.',
      monospace: true,
    },
  ],
  helpContent: {
    title: 'About Typeform Integration',
    bullets: [
      'Auto-registers a webhook with Typeform using your API token',
      'Links submissions to the full visitor journey: landing page, campaign and referrer',
      'Add an optional hidden field for precise session-level attribution',
    ],
    docsUrl: 'https://www.typeform.com/developers/webhooks/',
    docsLabel: 'Typeform Webhooks Guide',
    setupSteps: [
      'Generate a Personal Access Token from Typeform account → Settings → Personal tokens',
      'Paste the token above and click Save. The webhook is registered automatically',
      'Optional: Add a Hidden Field named "humblytics" to your Typeform',
      'Set its value via URL param: ?humblytics=SESSION_ID',
      'Submit a test form entry to verify the integration is working',
    ],
  },
}

/**
 * Tally connector configuration (webhook-only, no credentials needed)
 */
export const TALLY_CONNECTOR_CONFIG: Omit<ConnectorConfig, 'status' | 'enabled'> = {
  id: 'tally',
  name: 'Tally',
  description: 'Match Tally submits to sessions Humblytics already tracks.',
  longDescription: `Tally is a free form builder with a minimal, distraction-free interface. By connecting Tally to Humblytics, every form submission is automatically linked to the visitor session that completed the form.

This integration works via webhooks. Copy the webhook URL below and add it to your Tally form under Integrations → Webhooks. No credentials or API keys are required.

For accurate attribution, add a Hidden Field named "hmblViewId" to your Tally form. Tally automatically populates hidden fields from URL parameters. The Humblytics script handles the rest by appending the session ID to the form URL. No JavaScript required.`,
  brandKey: 'tally',
  category: 'forms',
  supportsTest: false,
  fields: [
    {
      id: 'webhookUrl',
      type: 'readonly',
      label: 'Webhook URL',
      placeholder: 'Loading...',
      required: false,
      helpText: 'Copy this URL and add it in Tally → Integrations → Webhooks. Click to copy.',
      monospace: true,
    },
  ],
  helpContent: {
    title: 'About Tally Integration',
    bullets: [
      'Reconcile each submission with the session Humblytics already recorded',
      'Link submissions to the full visitor journey: landing page, campaign and referrer',
      'Add a hidden field to your form for precise session-level attribution',
    ],
    docsUrl: 'https://tally.so/help/webhooks',
    docsLabel: 'Tally Webhooks Guide',
    setupSteps: [
      'Copy the Webhook URL above',
      'In Tally, open your form → Integrations → Webhooks',
      'Paste the URL and save',
      'Add a Hidden Field to your form named exactly "hmblViewId"',
      'Tally auto-populates it from the URL. The Humblytics script handles this automatically',
      'Submit a test form entry to verify the integration is working',
    ],
  },
}

/**
 * Webflow Forms connector configuration (site token + auto-registered webhook)
 * Coming soon — implementation complete, awaiting UX refinement.
 */
export const WEBFLOW_CONNECTOR_CONFIG: Omit<ConnectorConfig, 'status' | 'enabled'> = {
  id: 'webflow',
  name: 'Webflow',
  description: 'Match Webflow submits to sessions Humblytics already tracks.',
  comingSoon: true,
  longDescription: `Webflow is a visual web design platform. The Humblytics script already tracks when forms are submitted on your Webflow site, but the Webflow connector goes further. It receives form metadata via Webflow's webhook API.

This integration uses your Webflow Site Token to auto-register a webhook for form_submission events. Humblytics stores only form name and submission count (no field values).

Note: The Humblytics script and the Webflow connector can both be active simultaneously. The script captures session-level attribution; the connector provides form-level submission counts.`,
  brandKey: 'webflow',
  category: 'forms',
  supportsTest: true,
  fields: [
    {
      id: 'siteId',
      type: 'text',
      label: 'Webflow Site ID',
      placeholder: '5f4d...',
      required: true,
      helpText: 'Found in Webflow → Site settings → General → Site ID.',
    },
    {
      id: 'apiToken',
      type: 'password',
      label: 'Webflow Site Token',
      placeholder: 'paste your site token...',
      required: true,
      helpText: 'Generate from Webflow → Site settings → Integrations → API Access.',
      externalLink: {
        label: 'Webflow Dashboard',
        url: 'https://webflow.com/dashboard/sites',
      },
    },
    {
      id: 'webhookUrl',
      type: 'readonly',
      label: 'Registered Webhook URL',
      placeholder: 'Loading...',
      required: false,
      helpText: 'Auto-registered with Webflow when you save your credentials.',
      monospace: true,
    },
  ],
  helpContent: {
    title: 'About Webflow Forms Integration',
    bullets: [
      'Track form name and submission count (no field values stored)',
      'Webhook is auto-registered. No manual setup in Webflow needed',
      'Works alongside your Humblytics script for session attribution',
    ],
    privacyNote: 'We store only form name and submission count. Names, emails, and other field values from your forms are never stored, which helps you stay compliant with privacy regulations (GDPR, CCPA) and reducing data exposure.',
    docsUrl: 'https://developers.webflow.com/data/reference/webhooks/create-webhook',
    docsLabel: 'Webflow Webhooks Docs',
    setupSteps: [
      'Find your Webflow Site ID in Site settings → General',
      'Generate a Site Token in Site settings → Integrations → API Access',
      'Paste both values above and click Save',
      'Humblytics automatically registers a form_submission webhook with Webflow',
    ],
  },
}

/**
 * Google Search Console connector configuration (OAuth)
 * Coming soon — implementation complete, awaiting auth approval.
 */
const GSC_CONNECTOR_CONFIG: Omit<OAuthConnectorConfig, 'status' | 'enabled'> = {
  id: 'gsc',
  name: 'Google Search Console',
  description: 'Track organic search performance: clicks, impressions, CTR and position.',
  comingSoon: true,
  longDescription: `Google Search Console provides insights into how your site performs in Google Search. By connecting GSC to Humblytics, you can track clicks, impressions, click-through rate, and average position for your top queries and pages.

This integration uses OAuth to securely connect to your Google account with read-only access to your Search Console properties. Data is pulled daily with a 2-3 day lag (Google's standard delay).

With Search Console connected, you'll see which queries drive the most organic traffic, identify pages with low CTR that could be improved, and track position trends over time.`,
  brandKey: 'searchConsole',
  category: 'seo',
  authType: 'oauth',
  supportsTest: false,
  canReplaceNativeScript: false,
  fields: [],
  helpContent: {
    title: 'About Search Console Integration',
    bullets: [
      'Read-only access to your Search Console properties',
      'Track clicks, impressions, CTR, and average position',
      'Data updates daily with a 2-3 day lag',
    ],
    docsUrl: 'https://search.google.com/search-console/about',
    docsLabel: 'Search Console Help',
  },
}

/**
 * Humblytics native tracking script — always-on first-party data source.
 * Synthetic config so the Attribution wizard and Connectors page can iterate
 * a uniform list. Has no detail sheet; its Connect path is the install/onboarding flow.
 */
const HUMBLYTICS_TRACKING_CONFIG: Omit<ConnectorConfig, 'status' | 'enabled'> = {
  id: 'humblytics' as any,
  name: 'Humblytics Tracking',
  description: 'Sessions, UTM params, landing pages, referrer groups.',
  brandKey: 'humblytics' as any,
  category: 'traffic',
  supportsTest: false,
  fields: [],
  helpContent: {
    title: 'About Humblytics Tracking',
    bullets: [
      'First-party script. Enables heatmaps, funnels, and A/B experiments',
      'Required for accurate attribution',
    ],
  },
}

/**
 * Get connector config by type
 */
function getConnectorConfig(type: 'stripe' | 'foxy' | 'ga4' | 'meta' | 'google-ads' | 'jotform' | 'typeform' | 'tally' | 'webflow' | 'gsc'): Omit<ConnectorConfig | OAuthConnectorConfig, 'status' | 'enabled'> {
  switch (type) {
    case 'stripe':
      return STRIPE_CONNECTOR_CONFIG
    case 'foxy':
      return FOXY_CONNECTOR_CONFIG
    case 'ga4':
      return GA4_CONNECTOR_CONFIG
    case 'meta':
      return META_ADS_CONNECTOR_CONFIG
    case 'google-ads':
      return GOOGLE_ADS_CONNECTOR_CONFIG
    case 'jotform':
      return JOTFORM_CONNECTOR_CONFIG
    case 'typeform':
      return TYPEFORM_CONNECTOR_CONFIG
    case 'tally':
      return TALLY_CONNECTOR_CONFIG
    case 'webflow':
      return WEBFLOW_CONNECTOR_CONFIG
    case 'gsc':
      return GSC_CONNECTOR_CONFIG
    default:
      throw new Error(`Unknown connector type: ${type}`)
  }
}

/**
 * Active (credential-based) connector configs. Used by ConnectorSheet / detail pages.
 */
const ACTIVE_CONNECTORS = [
  STRIPE_CONNECTOR_CONFIG,
  FOXY_CONNECTOR_CONFIG,
  JOTFORM_CONNECTOR_CONFIG,
  TYPEFORM_CONNECTOR_CONFIG,
  TALLY_CONNECTOR_CONFIG,
  WEBFLOW_CONNECTOR_CONFIG,
]

/**
 * Forms connector configs
 */
const FORMS_CONNECTORS = [
  JOTFORM_CONNECTOR_CONFIG,
  TYPEFORM_CONNECTOR_CONFIG,
  TALLY_CONNECTOR_CONFIG,
  WEBFLOW_CONNECTOR_CONFIG,
]

/**
 * OAuth connector configs
 */
const OAUTH_CONNECTORS = [
  META_ADS_CONNECTOR_CONFIG,
  GA4_CONNECTOR_CONFIG,
  GOOGLE_ADS_CONNECTOR_CONFIG,
  GSC_CONNECTOR_CONFIG,
]

/**
 * Single source-of-truth list combining first-party tracking + OAuth + credential-based
 * connectors. Iterate this for both the Connectors page and the Attribution setup wizard
 * so the two views stay in lockstep. Each config carries its own `comingSoon` flag.
 */
export const ALL_CONNECTORS = [
  HUMBLYTICS_TRACKING_CONFIG,
  // Advertising
  META_ADS_CONNECTOR_CONFIG,
  GOOGLE_ADS_CONNECTOR_CONFIG,
  // Revenue
  STRIPE_CONNECTOR_CONFIG,
  FOXY_CONNECTOR_CONFIG,
  // Forms
  JOTFORM_CONNECTOR_CONFIG,
  TYPEFORM_CONNECTOR_CONFIG,
  TALLY_CONNECTOR_CONFIG,
  WEBFLOW_CONNECTOR_CONFIG,
  // SEO
  GSC_CONNECTOR_CONFIG,
]

type ConnectorCategory = ConnectorConfig['category']

/**
 * Filter ALL_CONNECTORS by category. Used by Connectors page sections (Website Traffic,
 * Advertising, Revenue, Forms, etc.) so each section is driven from the same list.
 */
export function getConnectorsByCategory(category: ConnectorCategory) {
  return ALL_CONNECTORS.filter((c) => c.category === category)
}

